import React, { useEffect, useState } from "react";
import { Megaphone } from "lucide-react";
import { toast } from "sonner";

import { useUser } from "util/UserContext";
import {
  updateAdBreakContentPreference,
  updateAdBreakConsentPreference,
  updateAllowContentPromotion,
  type AdBreakContentPreference,
  type AdBreakConsentPreference,
} from "util/userUtils";
import { getStoryUiStrings, resolveStoryLocale } from "i18n/storyUi";

const isAdBreakConsentPreference = (value: unknown): value is AdBreakConsentPreference =>
  value === "personalized" || value === "non_personalized";

const UserSettingsAdBreaks: React.FC = () => {
  const { currentUser } = useUser();
  const ui = getStoryUiStrings(resolveStoryLocale());
  const [consentPreference, setConsentPreference] = useState<AdBreakConsentPreference>("non_personalized");
  const [isSavingConsentPreference, setIsSavingConsentPreference] = useState(false);
  const [allowPromotion, setAllowPromotion] = useState(false);
  const [isSavingPromotionPreference, setIsSavingPromotionPreference] = useState(false);
  const [contentPreference, setContentPreference] = useState<AdBreakContentPreference>("promoted");
  const [isSavingContentPreference, setIsSavingContentPreference] = useState(false);
  // AdSense rollout is not yet live anywhere we know of; disable the
  // ad-vs-promoted inventory preference while keeping it visible.
  const adSenseEnabled = false;

  useEffect(() => {
    const storedConsent = currentUser?.properties?.ad_break_consent_preference?.value;
    if (isAdBreakConsentPreference(storedConsent)) {
      setConsentPreference(storedConsent);
    }

    const isPrivate = currentUser?.properties_public?.is_private === true;
    const storedPromotion = currentUser?.properties?.allow_content_promotion?.value;
    setAllowPromotion(typeof storedPromotion === "boolean" ? storedPromotion : !isPrivate);

    const storedContentPreference = currentUser?.properties?.ad_break_content_preference?.value;
    if (
      storedContentPreference === "promoted"
      || storedContentPreference === "ads"
      || storedContentPreference === "mixed"
    ) {
      setContentPreference(storedContentPreference);
    } else {
      setContentPreference("promoted");
    }
  }, [currentUser]);

  const handleConsentPreferenceChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextPreference = event.target.value;
    if (!isAdBreakConsentPreference(nextPreference)) {
      return;
    }
    const previousPreference = consentPreference;
    setConsentPreference(nextPreference);
    setIsSavingConsentPreference(true);
    try {
      const response = await updateAdBreakConsentPreference(nextPreference);
      if (currentUser) {
        currentUser.properties = {
          ...(currentUser.properties || {}),
          ad_break_consent_preference: response.data?.properties?.ad_break_consent_preference || {
            value: nextPreference,
          },
        };
      }
      toast.success(ui.adBreakConsentSaved);
    } catch (error) {
      console.error("Error updating ad_break_consent_preference:", error);
      setConsentPreference(previousPreference);
      toast.error(ui.adBreakConsentError);
    } finally {
      setIsSavingConsentPreference(false);
    }
  };

  const handleAllowPromotionChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.checked;
    setAllowPromotion(nextValue);
    setIsSavingPromotionPreference(true);
    try {
      await updateAllowContentPromotion(nextValue);
      if (currentUser) {
        currentUser.properties = {
          ...(currentUser.properties || {}),
          allow_content_promotion: { value: nextValue },
        };
      }
    } catch (error) {
      console.error("Error updating allow_content_promotion:", error);
      toast.error(ui.promoteError);
      setAllowPromotion(!nextValue);
    } finally {
      setIsSavingPromotionPreference(false);
    }
  };

  const handleContentPreferenceChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value as AdBreakContentPreference;
    const previousValue = contentPreference;
    setContentPreference(nextValue);
    setIsSavingContentPreference(true);
    try {
      await updateAdBreakContentPreference(nextValue);
      if (currentUser) {
        currentUser.properties = {
          ...(currentUser.properties || {}),
          ad_break_content_preference: { value: nextValue },
        };
      }
    } catch (error) {
      console.error("Error updating ad_break_content_preference:", error);
      toast.error(ui.promoteError);
      setContentPreference(previousValue);
    } finally {
      setIsSavingContentPreference(false);
    }
  };

  return (
    <div className="p-2">
      <div className="space-y-3">
        <div className="space-y-2">
          <label className="form-label flex items-center gap-2 text-base font-medium">
            <Megaphone className="size-4" aria-hidden="true" />
            {ui.navAdBreak}
          </label>
          <div className="group flex justify-between gap-3 rounded-md bg-eoe-bg-hover p-3 dark:bg-eoe-dark-bg-hover">
            <div className="flex flex-col items-start">
              <span className="text-sm text-eoe-text dark:text-eoe-dark-text">
                {ui.allowPromotionToggleLabel}
              </span>
              <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                {ui.allowPromotionToggleDescription}
              </p>
            </div>
            <input
              type="checkbox"
              checked={allowPromotion}
              disabled={isSavingPromotionPreference}
              onChange={handleAllowPromotionChange}
              className="toggle-switch-input col-span-1 mt-1 flex-shrink-0"
            />
          </div>

          <div
            className={`group flex flex-col gap-3 rounded-md p-3 ${
              adSenseEnabled
                ? "bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover"
                : "bg-eoe-bg-hover/60 dark:bg-eoe-dark-bg-hover/60"
            }`}
            title={!adSenseEnabled ? ui.adBreakPreferenceAdsComingSoon : undefined}
          >
            <div className="flex flex-col items-start">
              <span className="text-sm text-eoe-text dark:text-eoe-dark-text">
                {ui.adBreakPreferenceLabel}
              </span>
              {!adSenseEnabled ? (
                <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                  {ui.adBreakPreferenceAdsComingSoon}
                </p>
              ) : null}
            </div>
            <fieldset
              disabled={!adSenseEnabled || isSavingContentPreference}
              aria-disabled={!adSenseEnabled || isSavingContentPreference}
              className={`flex flex-col gap-2 ${adSenseEnabled ? "" : "opacity-60"}`}
            >
              <legend className="sr-only">{ui.adBreakPreferenceLabel}</legend>
              {(["promoted", "ads", "mixed"] as AdBreakContentPreference[]).map((value) => (
                <label key={value} className="flex items-center gap-2 text-sm text-eoe-text dark:text-eoe-dark-text">
                  <input
                    type="radio"
                    name="ad-break-content-preference"
                    value={value}
                    checked={contentPreference === value}
                    onChange={handleContentPreferenceChange}
                  />
                  <span>
                    {value === "promoted"
                      ? ui.adBreakPreferencePromoted
                      : value === "ads"
                        ? ui.adBreakPreferenceAds
                        : ui.adBreakPreferenceMixed}
                  </span>
                </label>
              ))}
            </fieldset>
          </div>

          <div className="group flex flex-col gap-3 rounded-md bg-eoe-bg-hover p-3 dark:bg-eoe-dark-bg-hover">
            <div className="flex flex-col items-start">
              <span className="text-sm text-eoe-text dark:text-eoe-dark-text">
                {ui.adBreakConsentPreferenceLabel}
              </span>
              <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                {ui.adBreakConsentPreferenceDescription}
              </p>
            </div>
            <fieldset disabled={isSavingConsentPreference} className="flex flex-col gap-2">
              <legend className="sr-only">{ui.adBreakConsentPreferenceLabel}</legend>
              <label className="flex items-center gap-2 text-sm text-eoe-text dark:text-eoe-dark-text">
                <input
                  type="radio"
                  name="ad-break-consent-preference"
                  value="non_personalized"
                  checked={consentPreference === "non_personalized"}
                  onChange={handleConsentPreferenceChange}
                />
                <span>{ui.adBreakConsentNonPersonalized}</span>
              </label>
              <label className="flex items-center gap-2 text-sm text-eoe-text dark:text-eoe-dark-text">
                <input
                  type="radio"
                  name="ad-break-consent-preference"
                  value="personalized"
                  checked={consentPreference === "personalized"}
                  onChange={handleConsentPreferenceChange}
                />
                <span>{ui.adBreakConsentPersonalized}</span>
              </label>
            </fieldset>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserSettingsAdBreaks;
